import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { useState, useEffect } from 'react';
import clubStyles from '../Club.module.scss';
import bsgStyles from './BallShoeGlove.module.scss';
import { createEmptyTourBag, normalizeString } from '@/utils';
import Tile from '@/common/Tile';
import PopoverMenu from '@/common/PopoverMenu';
import Edit from './Edit';
import { TourBagBsg } from '@/api/models';
import { useToast } from '@/context/ToastContext';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import ClubHistory from '../ClubHistory/ClubHistory';
import { headTypeMap, Action } from '@/constants';
import OfflineModal from '@/common/OfflineModal';
import Linkify from 'linkify-react';
import PhotoCaptureSection from '@/common/PhotoCaptureSection';

export default function BallShoeGlove() {
  const { tourBag, updateTourBag, player, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const isOnline = useOnlineStatus();
  const pageReadability = tourPageSettings?.bsg;
  const isFullAccess = pageReadability === 'full';

  // Ball shoe glove uses '' as the headTypeDescription sometimes
  let clubBag = tourBag?.find((bag) => bag.headTypeDescription?.toLowerCase() === 'ball,shoe,glove' || bag.headTypeDescription?.toString() == '');

  const [bsgBag, setBsgBag] = useState({} as TourBagBsg);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const { showToast } = useToast();
  const [showClubHistory, setShowClubHistory] = useState(false);
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [showPostEditOfflineModal, setShowPostEditOfflineModal] = useState(false);
  const hasSavedBsgRow = Boolean(bsgBag.rowGuid);

  useEffect(() => {
    if (clubBag && clubBag.tourBagBsg && clubBag.tourBagBsg.length > 0) {
      setBsgBag(clubBag.tourBagBsg[0]);
    } else {
      const tourBagBsg = {
        tourBagBsgId: -1,
        tourBagMstrId: clubBag?.tourBagMstrId || 0,
        playerId: player?.playerId,
        playerName: player?.fullName,
        tourId: clubBag?.tourId || player?.defaultTourId || 0,
        tourName: clubBag?.tourName || player?.defaultTourName || '',
        bsgBallId: 0,
        bsgBallName: '',
        bsgBallNote: '',
        bsgGloveId: 0,
        bsgGloveName: '',
        bsgGloveNote: '',
        bsgGloveSizeName: '',
        bsgGloveSizeId: 0,
        bsgHeadwearId: 0,
        bsgHeadwearName: '',
        bsgHeadwearNote: '',
        bsgShoeId: 0,
        bsgShoeSizeId: 0,
        bsgShoeName: '',
        bsgShoeNote: '',
        bsgShoeSizeName: '',
        bsgLeftShoeSizeName: '',
        bsgRightShoeSizeName: '',
        bsgShoeWidthName: '',
        bsgShoeColorName: '',
        tourBagMstrRowGuid: '',
        headTypeDescription: 'ball,shoe,glove',
        isActive: true,
        rowGuid: '',
        notes: '',
        golfBagName: '',
        golfBagLogo1: '',
        golfBagLogo2: '',
        golfBagLogo3: '',
        golfBagNotes: '',
        golfBagId: 0,
        modifyDate: new Date(),
        modifyUserGuid: clubBag?.modifyUserGuid || '',
        modifyUserName: clubBag?.modifyUserName || '',
        createDate: new Date(),
        createUserGuid: clubBag?.createUserGuid || '',
        editSource: 'web',
        tstamp: '',
      } as TourBagBsg;
      setBsgBag(tourBagBsg);
    }
  }, [tourBag, clubBag, player?.fullName, player?.playerId, player?.defaultTourId, player?.defaultTourName]);

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleCloseEditForm = () => {
    setShowEditForm(false);
  };

  const handleSaveEditForm = async (tourBagDetail: TourBagBsg) => {
    try {
      if (!clubBag) {
        clubBag = createEmptyTourBag('Ball,Shoe,Glove', player!);
      }
      if (isOnline) {
        setShowSpinner(true);
      }
      let thisClubBag = { ...clubBag, tourBagBsg: [tourBagDetail] };
      const action: Action = tourBagDetail.tourBagBsgId <= 0 ? 'CREATE' : 'UPDATE';
      await updateTourBag(thisClubBag, action, tourBagDetail.tourBagBsgId);
      if (!isOnline) {
        setShowPostEditOfflineModal(true);
      } else {
        setShowSpinner(false);
        showToast({ message: 'Saved', showCheckmark: true, status: 'success' });
      }
      setShowEditForm(false);
    } catch {
      setShowSpinner(false);
    }
  };

  const handleHistory = () => {
    setShowClubHistory(true);
  };

  const closeClubHistoryModal = () => {
    setShowClubHistory(false);
  };

  const openOfflineEditModal = () => {
    setShowPreEditOfflineModal(true);
  };

  const showEditFormOfflineModal = () => {
    setShowPreEditOfflineModal(false);
    handleEdit();
  };

  const handleClosePostEditOfflineModal = () => {
    setShowPostEditOfflineModal(false);
  };

  return (
    <>
      {showEditForm && <Edit saveEditForm={handleSaveEditForm} closeEditForm={handleCloseEditForm} tourBagBsg={bsgBag} />}
      {!showEditForm && (
        <div className={clubStyles.container}>
          <div className={bsgStyles.container}>
            <div className={bsgStyles.form}>
              {isFullAccess && isSyncComplete && (
                <div className={bsgStyles.popoverMenu}>
                  <PopoverMenu onEdit={!isOnline ? openOfflineEditModal : handleEdit} onHistory={handleHistory} index={0} showHistory={isOnline && permissions?.tourBagSettings?.clubHistory} />
                </div>
              )}
              <div className={bsgStyles.row}>
                <div className={clubStyles.categoryName}>Ball</div>
                <div className={clubStyles.categoryTitle}>{pageReadability === 'limitedView' && bsgBag.bsgBallShortName ? bsgBag.bsgBallShortName : bsgBag.bsgBallName}</div>
              </div>
              <div className={`${clubStyles.tileRow} + ${bsgStyles.rowMarginBottom}`}>
                <Tile label="Ball Note" value={normalizeString(bsgBag.bsgBallNote)} variant="xxl" />
              </div>
              <div className={bsgStyles.row}>
                <div className={clubStyles.categoryName}>Glove</div>
                <div className={clubStyles.categoryTitle}>{bsgBag.bsgGloveName}</div>
              </div>
              <div className={`${clubStyles.tileRow} + ${bsgStyles.rowMarginBottom}`}>
                <Tile label="Glove Size" value={normalizeString(bsgBag.bsgGloveSizeName)} variant="large" />
                <Tile label="Glove Note" value={normalizeString(bsgBag.bsgGloveNote)} variant="xxl" />
              </div>
              <div className={bsgStyles.row}>
                <div className={clubStyles.categoryName}>Headwear</div>
                <div className={clubStyles.categoryTitle}>{bsgBag.bsgHeadwearName}</div>
              </div>
              <div className={`${clubStyles.tileRow} + ${bsgStyles.rowMarginBottom}`}>
                <Tile label="Headwear Note" value={normalizeString(bsgBag.bsgHeadwearNote)} variant="xxl" />
              </div>
              <div className={bsgStyles.row}>
                <div className={clubStyles.categoryName}>Shoe</div>
                <div className={clubStyles.categoryTitle}>{bsgBag.bsgShoeName}</div>
              </div>
              <div className={`${clubStyles.tileRow} + ${bsgStyles.rowMarginBottom}`}>
                <Tile label="Size" value={normalizeString(bsgBag.bsgShoeSizeName)} variant="xxl" />
                <Tile label="Width" value={normalizeString(bsgBag.bsgShoeWidthName)} variant="xxl" />
              </div>
              <div className={bsgStyles.row}>
                <div className={clubStyles.categoryName}>Golf Bag</div>
                <div className={clubStyles.categoryTitle}>{bsgBag.golfBagName}</div>
              </div>
              <div className={`${clubStyles.tileRow} + ${bsgStyles.rowMarginBottom}`}>
                <Tile label="Bag Note" value={normalizeString(bsgBag.golfBagNotes)} variant="xxl" />
                <Tile label="Name on Bag" value={normalizeString(bsgBag.nameOnBag)} variant="large" />
                <Tile label="Bag Logo 1" value={normalizeString(bsgBag.golfBagLogo1)} variant="large" />
                <Tile label="Bag Logo 2" value={normalizeString(bsgBag.golfBagLogo2)} variant="large" />
                <Tile label="Bag Logo 3" value={normalizeString(bsgBag.golfBagLogo3)} variant="large" />
              </div>
              <div className={bsgStyles.row}>
                <div className={clubStyles.categoryName}>General Notes</div>
              </div>
              <div className={`${clubStyles.tileRow} + ${bsgStyles.rowMarginBottom}`}>
                <div className={`${clubStyles.notes}`}>
                  <Linkify options={{ target: '_blank' }}>{normalizeString(bsgBag.notes)}</Linkify>
                </div>
              </div>
              {hasSavedBsgRow && <PhotoCaptureSection playerId={bsgBag.playerId || player?.playerId || 0} ownerRowGuid={bsgBag.rowGuid} imageMetadata={bsgBag.imageMetadata} isBsg />}
            </div>
          </div>
        </div>
      )}
      {showClubHistory && (
        <ClubHistory
          tourBagMstrId={clubBag?.tourBagMstrId || 0}
          rowGuid={clubBag?.tourBagBsg[0].rowGuid || ''}
          headTypeId={headTypeMap['Ball,Shoe,Glove']}
          player={player}
          model={clubBag?.headTypeDescription || ''}
          closeModal={closeClubHistoryModal}
        />
      )}
      {showSpinner && <SpinnerOverlay />}
      <OfflineModal onCancel={() => setShowPreEditOfflineModal(false)} onClose={showEditFormOfflineModal} isOpen={showPreEditOfflineModal} modalType={'IsOffline'} />
      <OfflineModal onCancel={() => {}} onClose={handleClosePostEditOfflineModal} isOpen={showPostEditOfflineModal} modalType={'ConfirmSave'} />
    </>
  );
}
